// PatientFooter.jsx
import React from "react";
import {
  Home,
  Compass,
  MapPin,
  MessageCircle,
  LayoutDashboard,
} from "lucide-react";

const PatientFooter = ({
  activeTab = "home",
  unreadMessages = 0,
  elevation = true,
}) => {
  const spacerHeight = 72;

  const tabs = [
    { key: "dashboard", label: "Profile", Icon: LayoutDashboard },
    { key: "home", label: "Home", Icon: Home },
    { key: "explore", label: "Explore", Icon: Compass },
    { key: "map", label: "Harta", Icon: MapPin },
    { key: "messages", label: "Mesazhe", Icon: MessageCircle },
    
  ];

  const handleTabClick = (key) => {
    // notify PatientHome (and anybody else) to switch tab
    window.dispatchEvent(
      new CustomEvent("mikdok:navigateTab", {
        detail: { tab: key },
      })
    );
  };

  return (
    <>
      {/* space so content doesn’t hide behind footer */}
      <div aria-hidden="true" style={{ height: spacerHeight }} />

      <nav
        role="navigation"
        aria-label="Patient footer"
        style={styles.nav(elevation)}
      >
        <style>{css}</style>

        <div className="footer-wrap">
          <ul
            className="footer-grid"
            role="list"
            style={{
              gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))`,
            }}
          >
            {tabs.map(({ key, label, Icon }) => {
              const isActive = activeTab === key;
              const isMessages = key === "messages";
              const showBadge = isMessages && unreadMessages > 0;

              return (
                <li key={key}>
                  <button
                    className={`footer-btn ${
                      isActive ? "footer-btn--active" : ""
                    }`}
                    aria-label={label}
                    aria-current={isActive ? "page" : undefined}
                    onClick={() => handleTabClick(key)}
                    title={label}
                  >
                    <div style={{ position: "relative" }}>
                      <Icon className="footer-icon" aria-hidden />
                      {showBadge && (
                        <span
                          style={{
                            position: "absolute",
                            top: -4,
                            right: -8,
                            minWidth: 16,
                            height: 16,
                            padding: "0 4px",
                            borderRadius: 999,
                            background: "#ef4444",
                            color: "#fff",
                            fontSize: 10,
                            fontWeight: 700,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {unreadMessages > 9 ? "9+" : unreadMessages}
                        </span>
                      )}
                    </div>
                    <span className="footer-label">{label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </nav>
    </>
  );
};

const styles = {
  nav: (elevation) => ({
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 50,
    background: "#fff",
    borderTop: "1px solid rgba(229,231,235,0.9)",
    boxShadow: elevation ? "0 -8px 24px -16px rgba(0,0,0,0.18)" : "none",
    backdropFilter: "saturate(120%) blur(10px)",
    WebkitBackdropFilter: "saturate(120%) blur(10px)",
  }),
};

const css = `
  .footer-wrap {
    position: relative;
    max-width: 1100px;
    margin: 0 auto;
    padding: 10px 16px 18px;
    padding-bottom: calc(18px + env(safe-area-inset-bottom, 0px));
  }

  .footer-grid {
    display: grid;
    gap: 4px;
    align-items: center;
  }
  .footer-grid > li { list-style: none; }

  .footer-btn {
    appearance: none;
    -webkit-appearance: none;
    background: transparent;
    border: none;
    border-radius: 12px;
    padding: 10px 6px;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    transition: transform .15s ease, background .15s ease, box-shadow .15s ease;
    color: #4b5563;
  }

  @media (hover:hover) and (pointer:fine){
    .footer-btn:hover { background: #f3f4f6; }
  }

  .footer-btn:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(37,99,235,0.35);
    background: #f3f4f6;
  }

  .footer-btn--active {
    color: #0a66c2;
    background: #eef6ff;
    border: 1px solid rgba(10,102,194,0.25);
  }

  .footer-label {
    font-size: 11px;
    line-height: 1;
    font-weight: 700;
    letter-spacing: .2px;
  }

  .footer-icon { width: 20px; height: 20px; }

  @media (prefers-reduced-motion: reduce) {
    .footer-btn { transition: none; }
  }
  @supports not (backdrop-filter: blur(10px)) {
    nav[role="navigation"] { background: #fff; }
  }
`;

export default React.memo(PatientFooter);
